// Central analytics module (PostHog).
//
// Everything funnels through here so event names stay consistent and the whole
// app degrades gracefully to a no-op when no PostHog key is configured.
//
// The browser only ever uses the *project* API key (starts with `phc_`, safe to
// be public). The *personal* key (`phx_`) is a secret and must NEVER appear in
// client code — it's only used server-side by scripts/optimize-experiments.mjs.

import posthog from 'posthog-js';
import { isMobile } from 'react-device-detect';

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const APP_VERSION = process.env.REACT_APP_VERSION || '0.1.0';

let enabled = false;

// Property keys never sent to PostHog (belt-and-suspenders — we already send only
// lengths/booleans/ids, never raw secrets). Applied by posthog-js via
// property_denylist. IMPORTANT: do NOT include generic names like `token` here —
// posthog-js stores its own `token` inside each event's properties, and stripping
// it makes the server silently drop the event.
const PII_DENYLIST = [
  'password', 'confirmPassword', 'confirm_password', 'newPassword',
  'client_secret', 'clientSecret', 'ipAddress', 'cardNumber', 'cvc',
];

// Strip query strings/hashes from URL-valued properties so secrets that live in
// URLs never reach PostHog: the Stripe `payment_intent_client_secret` on the
// /processing return route, and `?token=` on verify-email / password-reset
// routes, would otherwise ride along in $current_url / $referrer / autocaptured
// element hrefs (and the expiring signed GCS URLs on audio download links).
// IMPORTANT: this only REWRITES url values — it never deletes keys. Deleting a
// key (e.g. posthog-js's own `token`) makes the ingestion server silently drop
// the whole event; that regression is exactly why we don't denylist `token`.
// `referrer` is our own first-touch super property (document.referrer); after a
// full-page redirect away from /forgot-password or /verify-email it can hold
// the reset/verification URL, so it is sanitized like PostHog's own $referrer.
export const URL_PROP_KEYS = ['$current_url', '$referrer', '$referring_domain', 'referrer', 'landing_route', 'route', 'page_path', 'page_location'];
export function stripUrlQuery(value) {
  if (typeof value !== 'string') return value;
  const cut = value.search(/[?#]/);
  return cut >= 0 ? value.slice(0, cut) : value;
}
export function sanitizeProperties(properties) {
  if (!properties || typeof properties !== 'object') return properties;
  try {
    for (const k of URL_PROP_KEYS) {
      if (typeof properties[k] === 'string') properties[k] = stripUrlQuery(properties[k]);
    }
    // Autocapture nests clicked-element metadata (incl. anchor hrefs, e.g. the
    // signed audio-download URL) under $elements — strip their query too.
    if (Array.isArray(properties.$elements)) {
      for (const el of properties.$elements) {
        if (el && typeof el.href === 'string') el.href = stripUrlQuery(el.href);
      }
    }
  } catch (e) {
    /* never break capture */
  }
  return properties;
}

export function initAnalytics({ privateWritingSurface = false } = {}) {
  if (enabled) return;
  if (!KEY) {
    // eslint-disable-next-line no-console
    console.info('[analytics] REACT_APP_POSTHOG_KEY not set — PostHog disabled.');
    return;
  }
  if (typeof KEY === 'string' && KEY.startsWith('phx_')) {
    // eslint-disable-next-line no-console
    console.error(
      '[analytics] REACT_APP_POSTHOG_KEY looks like a PERSONAL key (phx_). ' +
        'Use the PROJECT key (phc_) instead. PostHog disabled.'
    );
    return;
  }
  posthog.init(KEY, {
    api_host: HOST,
    capture_pageview: false, // we fire $pageview manually on route change (SPA)
    capture_pageleave: true,
    capture_exceptions: false, // our handlers attach area/release context; avoid duplicate reports
    capture_dead_clicks: false,
    capture_heatmaps: false,
    autocapture: !privateWritingSurface,
    rageclick: !privateWritingSurface,
    // Writing also appears outside inputs in the main app. Keep replay off
    // and remove text/attributes from automatic interaction events everywhere.
    // Library surfaces additionally use only explicit, text-free events.
    disable_session_recording: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    person_profiles: 'identified_only', // keep anonymous volume low; funnels still work
    session_recording: { maskAllInputs: true }, // never record form fields (passwords/cards/lyrics)
    property_denylist: PII_DENYLIST,
    sanitize_properties: sanitizeProperties, // strip secrets that live in URLs
  });
  enabled = true;

  registerBaseProps();
  sanitizeStoredReferrer();

  // Global error capture (works regardless of posthog-js version's autocapture support).
  if (typeof window !== 'undefined') {
    window.addEventListener('error', (e) => {
      if (!e.error && !e.message) return; // resource events are handled at their request boundary
      captureException(e.error || new Error(e.message), { source: 'window.onerror', filename: stripUrlQuery(e.filename), line: e.lineno, column: e.colno });
    });
    window.addEventListener('unhandledrejection', (e) =>
      captureException(e.reason, { source: 'unhandledrejection' })
    );
  }
}

// Base super properties on every event, for segmentation. Re-applied after
// posthog.reset(), which clears every super property.
function registerBaseProps() {
  try {
    posthog.register({ app_version: APP_VERSION, is_mobile: isMobile });
  } catch (e) {
    /* noop */
  }
}

// posthog-js stores the first referrer it sees and sends it as the
// $initial_referrer person property via $set_once, which sanitize_properties
// never sees. Strip its query/hash in persistence before the first event.
function sanitizeStoredReferrer() {
  try {
    const props = posthog.persistence?.props;
    const info = props?.$initial_referrer_info;
    if (info && typeof info.$referrer === 'string') {
      const clean = stripUrlQuery(info.$referrer);
      if (clean !== info.$referrer) {
        props.$initial_referrer_info = { ...info, $referrer: clean };
        posthog.persistence.save?.();
      }
    }
  } catch (e) {
    /* noop */
  }
}

export function isAnalyticsEnabled() {
  return enabled;
}

// Fire a product/funnel event. Safe to call even when analytics is disabled.
export function track(event, properties) {
  if (!enabled) return;
  try {
    posthog.capture(event, properties);
  } catch (e) {
    /* never let analytics break the app */
  }
}

// Register super properties (attached to ALL subsequent events). Use for
// session-wide segmentation dimensions like is_logged_in / plan / auth_method.
export function registerSuperProps(props) {
  if (!enabled || !props) return;
  try {
    posthog.register(props);
  } catch (e) {
    /* noop */
  }
}

// Register super properties only if they are not already set (first-touch).
export function registerSuperPropsOnce(props) {
  if (!enabled || !props) return;
  try {
    posthog.register_once(props);
  } catch (e) {
    /* noop */
  }
}

// Set/merge person properties on the current (identified) profile.
export function setPersonProperties(props) {
  if (!enabled || !props) return;
  try {
    posthog.setPersonProperties(props);
  } catch (e) {
    /* noop */
  }
}

// Set person properties only if not already set (first-touch attribution etc.).
export function setPersonPropertiesOnce(props) {
  if (!enabled || !props) return;
  try {
    posthog.setPersonProperties(undefined, props);
  } catch (e) {
    /* noop */
  }
}

// Derive non-sensitive billing/plan traits from the app's user object.
function deriveUserTraits(user) {
  if (!user) return {};
  const status = user.customer?.stripeSubscriptionStatus;
  const traits = {};
  if (status) traits.subscription_status = status;
  traits.is_subscribed = status === 'active' || status === 'Active';
  if (typeof user.oneOffCustomer !== 'undefined') traits.is_oneoff_customer = !!user.oneOffCustomer;
  if (user.customer?.plan) traits.plan = user.customer.plan;
  return traits;
}

// Tie subsequent events to a known user (stitches anonymous → signed-in for
// funnels). Only sends non-sensitive traits: never email, name or other
// contact details (see ANALYTICS.md). extraProps merges in caller context
// (e.g. { auth_method: 'password' }).
export function identifyUser(user, extraProps = {}) {
  if (!enabled || !user) return;
  const id = user._id || user.id || user.customer?.stripeId;
  if (!id) return;
  try {
    posthog.identify(String(id), {
      ...deriveUserTraits(user),
      ...extraProps,
    });
    // Keep is_subscribed/plan queryable as super props too.
    registerSuperProps(deriveUserTraits(user));
  } catch (e) {
    /* noop */
  }
}

// Call on logout so the next visitor isn't merged into the previous user.
// Clears the previous user's persisted experiment assignments and re-applies
// the base super properties that posthog.reset() wipes. Callers register
// is_logged_in AFTER this, so it isn't wiped too.
export function resetAnalytics() {
  if (!enabled) return;
  try {
    posthog.reset();
  } catch (e) {
    /* noop */
  }
  clearExperimentAssignments();
  registerBaseProps();
}

export const EXPERIMENT_STORAGE_PREFIX = 'exp_v2_';
function clearExperimentAssignments() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(EXPERIMENT_STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch (e) {
    /* storage unavailable */
  }
}

// The SDK supplies the structured exception list, parsed stack frames and
// grouping metadata required by PostHog Error Tracking.
export function captureException(error, context = {}) {
  if (!enabled || !error) return;
  try {
    const exception = error instanceof Error || typeof error?.message === 'string' ? error : new Error(typeof error === 'string' ? error : 'Unexpected application error');
    // URLs in stack traces/messages can contain signed media or auth queries.
    const clean = (value) => typeof value === 'string' ? value.replace(/https?:\/\/[^\s)]+/g, (url) => stripUrlQuery(url)) : value;
    const safe = new Error(clean(exception.message));
    safe.name = exception.name;
    safe.stack = clean(exception.stack);
    posthog.captureException(safe, { ...context, app_version: APP_VERSION, route: typeof window !== 'undefined' ? window.location.pathname : undefined });
  } catch (e) {
    /* noop */
  }
}

// Canonical event taxonomy. Use these constants everywhere so funnel steps line
// up in the PostHog UI (a typo'd event name silently breaks a funnel step).
export const EVENTS = {
  // Acquisition / landing
  LANDING_CTA_CLICKED: 'landing_cta_clicked',
  // Auth & onboarding
  REGISTRATION_STARTED: 'registration_started',
  REGISTRATION_COMPLETED: 'registration_completed',
  REGISTRATION_FAILED: 'registration_failed',
  SIGN_IN_STARTED: 'sign_in_started',
  SIGNED_IN: 'signed_in',
  SIGN_IN_FAILED: 'sign_in_failed',
  LOGGED_OUT: 'logged_out',
  GOOGLE_AUTH_STARTED: 'google_auth_started',
  GOOGLE_AUTH_SUCCEEDED: 'google_auth_succeeded',
  GOOGLE_AUTH_FAILED: 'google_auth_failed',
  EMAIL_VERIFICATION_VIEWED: 'email_verification_viewed',
  EMAIL_VERIFICATION_SUCCEEDED: 'email_verification_succeeded',
  EMAIL_VERIFICATION_FAILED: 'email_verification_failed',
  PASSWORD_RESET_REQUESTED: 'password_reset_requested',
  PASSWORD_RESET_REQUEST_FAILED: 'password_reset_request_failed',
  PASSWORD_RESET_COMPLETED: 'password_reset_completed',
  PASSWORD_RESET_FAILED: 'password_reset_failed',
  // Navigation
  NAV_LINK_CLICKED: 'nav_link_clicked',
  SIDEBAR_NAV_CLICKED: 'sidebar_nav_clicked',
  // Core product — lyrics generation
  LYRICS_GENERATION_STARTED: 'lyrics_generation_started',
  LYRICS_GENERATION_COMPLETED: 'lyrics_generation_completed',
  LYRICS_GENERATION_FAILED: 'lyrics_generation_failed',
  LYRICS_IMPROVED: 'lyrics_improved',
  NEXT_LINE_SUGGESTED: 'next_line_suggested',
  RELATED_WORDS_SEARCHED: 'related_words_searched',
  GENERATION_LANGUAGE_SELECTED: 'generation_language_selected',
  RESULT_COPIED: 'result_copied',
  RESULT_SHARED_OPENED: 'result_shared_opened',
  ADVANCED_MODEL_BLOCKED: 'advanced_model_blocked',
  CREDIT_LIMIT_HIT: 'credit_limit_hit',
  // Core product — music generation
  MUSIC_GENERATION_STARTED: 'music_generation_started',
  MUSIC_GENERATION_COMPLETED: 'music_generation_completed',
  MUSIC_GENERATION_FAILED: 'music_generation_failed',
  TRACK_PLAYED: 'track_played',
  TRACK_DOWNLOADED: 'track_downloaded',
  TRACK_LOAD_ERROR: 'track_load_error',
  TRACK_PLAYBACK_BLOCKED: 'track_playback_blocked',
  TRACK_PLAYBACK_COMPLETED: 'track_playback_completed',
  // Sharing / virality
  LYRICS_SHARED: 'lyrics_shared',
  SHARE_IMAGE_DOWNLOADED: 'share_image_downloaded',
  // Monetisation funnel
  PRICING_VIEWED: 'pricing_viewed',
  PLAN_SELECTED: 'plan_selected',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_FAILED: 'checkout_failed',
  ONEOFF_PAYMENT_STARTED: 'oneoff_payment_started',
  PURCHASE_COMPLETED: 'purchase_completed',
  PAYMENT_FAILED: 'payment_failed',
  SUBSCRIBE_PAYMENT_SUBMITTED: 'subscribe_payment_submitted',
  // Billing lifecycle
  BILLING_ACCOUNT_VIEWED: 'billing_account_viewed',
  SUBSCRIPTION_CANCEL_INTENT: 'subscription_cancel_intent',
  SUBSCRIPTION_CANCEL_CONFIRMED: 'subscription_cancel_confirmed',
  SUBSCRIPTION_CANCEL_DISMISSED: 'subscription_cancel_dismissed',
  SUBSCRIPTION_REACTIVATED: 'subscription_reactivated',
  // Upsell modal (credit limit)
  CREDIT_LIMIT_MODAL_VIEWED: 'credit_limit_modal_viewed',
  CREDIT_LIMIT_MODAL_CTA_CLICKED: 'credit_limit_modal_cta_clicked',
  INVITE_SENT: 'invite_sent',
  // Reliability
  API_REQUEST_FAILED: 'api_request_failed',
  AUTH_SESSION_EXPIRED: 'auth_session_expired',
  WEB_VITAL: 'web_vital',
  // Design-refresh Studio / projects (from the parallel design-refresh instrumentation)
  STUDIO_OPENED: 'studio_opened',
  PROJECT_CREATED: 'project_created',
  PROJECT_OPENED: 'project_opened',
  PROJECT_EXPORTED: 'project_exported',
  RECORDING_STARTED: 'recording_started',
  RECORDING_COMPLETED: 'recording_completed',
  LYRICS_SAVED: 'lyrics_saved',
  // Studio stem tools
  STEMS_SPLIT_STARTED: 'stems_split_started',
  STEMS_SPLIT_COMPLETED: 'stems_split_completed',
  PART_REPLACE_STARTED: 'part_replace_started',
  PART_REPLACE_COMPLETED: 'part_replace_completed',
  STEM_TOOL_FAILED: 'stem_tool_failed',
};

// A/B experiment feature-flag keys. Each has a matching multivariate flag (and
// draft Experiment) in PostHog; the optimizer (scripts/optimize-experiments.mjs)
// reallocates traffic across variants based on measured conversion.
export const EXPERIMENTS = {
  LANDING_HERO_HEADLINE: 'landing-hero-headline',
  LANDING_CTA_COPY: 'landing-cta-copy',
  PRICING_DEFAULT_TAB: 'pricing-default-tab',
  PRICING_CTA_COPY: 'pricing-cta-copy',
  CREDIT_MODAL_CTA: 'credit-modal-cta',
  DEFAULT_CREATIVITY_TEMP: 'default-creativity-temp',
};

export { posthog };
